import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../utils/apiUrl';
import {
  ShieldCheck, ShieldAlert, ShieldX, ExternalLink, MessageCircle, Globe,
  ClipboardCheck, CheckCircle2, FileText, History, Sparkles, Clock, Hash,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import VideoPlayer from '../components/VideoPlayer';
import TestettiKVideoPlayer from '../components/TestettiKVideoPlayer';
import { canRenderPublicVideo } from '../utils/videoUrlValidation';

// TT-ROZET-001-FINAL — Full-page expiry lock (badge inactive / expired / null date).
// Dark green (#1C3B2A), centered, white text, NO site nav, NO product content, mobile responsive.
const BadgeExpiredPage = () => (
  <div
    className="min-h-screen w-full flex flex-col items-center justify-center px-6 py-12"
    style={{ background: '#1C3B2A' }}
    data-testid="verify-badge-expired-page"
  >
    <div className="max-w-lg text-center">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-8 border-2 border-white/40" aria-hidden="true">
        <ShieldX className="w-10 h-10 text-white" strokeWidth={1.75} />
      </div>
      <h1 className="text-white text-2xl md:text-3xl font-black tracking-tight mb-5" data-testid="verify-badge-expired-title">
        Güven Rozeti Süresi Dolmuştur
      </h1>
      <p
        className="text-white text-[15px] md:text-base leading-relaxed mb-10 whitespace-pre-line"
        style={{ opacity: 0.9 }}
        data-testid="verify-badge-expired-body"
      >
        {`Testlerini görmek istediğiniz ürünün TestettiK® Güven Rozeti süresi dolmuştur.

Ürünün güncel test bilgilerine ulaşmak için lütfen satıcıdan TestettiK® Güven Rozeti talebinde bulununuz.`}
      </p>
      <div className="flex flex-col items-center gap-2 pt-8 border-t border-white/20">
        <img
          src="https://customer-assets.emergentagent.com/job_trust-badge-4/artifacts/logo-testettik-white.png"
          alt="TestettiK"
          className="h-6 opacity-90"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        <p className="text-white text-xs font-semibold tracking-wider" style={{ opacity: 0.75 }}>
          testettik.com
        </p>
      </div>
    </div>
  </div>
);


// ── State-specific copy per spec ──
const STATE_COPY = {
  expired: {
    title: 'Test geçerlilik süresi dolmuştur.',
    description: 'Bu ürün için oluşturulan TestettiK® doğrulama bağlantısının geçerlilik süresi sona ermiştir. Satıcınızdan güncel TestettiK® Güven Rozeti isteyiniz.',
    ctaLabel: 'Yayındaki doğrulanmış ürünleri incele',
    ctaTo: '/products',
    tone: 'amber',
  },
  invalid: {
    title: 'Doğrulama bağlantısı aktif değil.',
    description: 'Bu bağlantı artık aktif değildir. Ürünün güncel TestettiK® doğrulama durumunu satıcınızdan talep edebilirsiniz.',
    ctaLabel: 'Ana sayfa',
    ctaTo: '/',
    tone: 'neutral',
  },
  not_public: {
    title: 'Doğrulama sayfası henüz yayında değil.',
    description: 'Bu ürünün TestettiK® doğrulama sayfası henüz yayınlanmamıştır.',
    ctaLabel: 'Ana sayfa',
    ctaTo: '/',
    tone: 'neutral',
  },
};

const formatDateTR = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch { return null; }
};

// ── Reusable Section Card ──
const SectionCard = ({ icon, title, subtitle, children, testid, className = '' }) => (
  <section className={`border rounded-xl bg-white shadow-sm ${className}`} style={{ borderColor: 'var(--border-soft)' }} data-testid={testid}>
    <header className="flex items-center gap-3 px-5 md:px-6 pt-5 pb-3">
      <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--bg-accent-soft)', color: 'var(--green-main)' }} aria-hidden="true">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-base md:text-lg font-black tracking-tight" style={{ color: 'var(--text-primary)' }}>{title}</h3>
        {subtitle && <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>{subtitle}</p>}
      </div>
    </header>
    <div className="px-5 md:px-6 pb-5 md:pb-6">
      {children}
    </div>
  </section>
);

// ── Invalid / Expired / Not-Public state ──
const VerifyStatePage = ({ state, slug }) => {
  const copy = STATE_COPY[state] || STATE_COPY.invalid;
  const isExpired = state === 'expired';
  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--bg-section)' }} data-testid={`verify-state-${state}`}>
      <Header />
      <main className="flex-1 px-4 py-16 md:py-24">
        <div className="max-w-md mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{
            background: isExpired ? '#FEF3C7' : '#F3F4F6',
            color: isExpired ? '#92400E' : '#374151',
          }} aria-hidden="true">
            <ShieldAlert className="w-7 h-7" />
          </div>
          <h1 className="text-2xl md:text-3xl font-black tracking-tight mb-3" style={{ color: 'var(--text-primary)' }} data-testid="verify-state-title">
            {copy.title}
          </h1>
          <p className="text-[15px] leading-relaxed font-medium mb-8" style={{ color: 'var(--text-secondary-strong)' }} data-testid="verify-state-description">
            {copy.description}
          </p>
          <Link to={copy.ctaTo}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition hover:-translate-y-0.5 hover:shadow-md"
            style={{ background: 'var(--green-main)', color: '#FFF' }}
            data-testid="verify-state-cta">
            {copy.ctaLabel}
          </Link>
          {slug && (
            <p className="text-[11px] font-medium mt-8 inline-flex items-center gap-1.5" style={{ color: 'var(--text-muted)' }}>
              <Hash className="w-3 h-3" /> Doğrulama Kodu: <span className="font-mono font-bold">{slug}</span>
            </p>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

const VerifyPage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [errorState, setErrorState] = useState(null); // 'invalid' | 'expired' | 'not_public' | 'badge_expired'
  const [loading, setLoading] = useState(true);
  const viewTracked = useRef(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/verify/${slug}`)
      .then(({ data }) => {
        // TT-ROZET-001-FINAL — badge validity gate. Short-circuit renders BadgeExpiredPage.
        if (data && data.badge_valid === false) {
          setErrorState('badge_expired');
          setLoading(false);
          return;
        }
        // Backend now returns link_state — respect it strictly
        if (data.link_state && data.link_state !== 'active') {
          setErrorState(data.link_state);
          setLoading(false);
          return;
        }
        setData(data);
        document.title = `${data.name || 'Ürün'} Doğrulama | TestettiK®`;
        const ogTitle = `${data.name || 'Ürün'} Test Sonucu — ${data.score?.toFixed(1) || '?'}/10`.slice(0, 60);
        const ogDesc = (data.short_description || 'Bu ürün TestettiK® tarafından test edilmiştir.').slice(0, 150);
        const ogImage = data.image_url && data.image_url.startsWith('http') ? data.image_url : 'https://www.testettik.com/og-default.png';
        const setMeta = (prop, content) => {
          let el = document.querySelector(`meta[property="${prop}"]`) || document.querySelector(`meta[name="${prop}"]`);
          if (!el) { el = document.createElement('meta'); el.setAttribute(prop.startsWith('og:') ? 'property' : 'name', prop); document.head.appendChild(el); }
          el.setAttribute('content', content);
        };
        setMeta('og:title', ogTitle); setMeta('og:description', ogDesc); setMeta('og:image', ogImage);
        setMeta('og:url', `https://www.testettik.com/v/${slug}`); setMeta('og:type', 'product');
        setMeta('twitter:card', 'summary_large_image'); setMeta('twitter:title', ogTitle);
        setMeta('twitter:description', ogDesc); setMeta('twitter:image', ogImage);
        if (!viewTracked.current) {
          viewTracked.current = true;
          const trackPayload = JSON.stringify({ slug, source: 'direct' });
          if (navigator.sendBeacon) {
            navigator.sendBeacon(`${API_URL}/api/track/verification-view`, new Blob([trackPayload], { type: 'application/json' }));
          } else {
            fetch(`${API_URL}/api/track/verification-view`, { method: 'POST', body: trackPayload, headers: { 'Content-Type': 'application/json' }, keepalive: true }).catch(() => { });
          }
        }
      })
      .catch(err => {
        // 404 with structured detail → invalid; otherwise generic invalid
        setErrorState('invalid');
        console.warn('verify fetch failed', err?.response?.status);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-section)' }}>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Yükleniyor…</p>
      </div>
    );
  }

  if (errorState === 'badge_expired') return <BadgeExpiredPage />;
  if (errorState) return <VerifyStatePage state={errorState} slug={slug} />;
  if (!data) return <VerifyStatePage state="invalid" slug={slug} />;

  const productName = data.name || 'Ürün bilgisi';
  const brandName = data.brand || '';
  const isPassed = (data.score || 0) >= 7 && data.verification_active;
  const isFailed = (data.score || 0) < 7;
  const sellerAction = data.seller_action;
  const hasSellerAction = sellerAction && sellerAction.type;

  const handleSellerClick = (e, action) => {
    const trackPayload = JSON.stringify({ slug, source: 'direct' });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API_URL}/api/track/seller-click`, new Blob([trackPayload], { type: 'application/json' }));
    } else {
      fetch(`${API_URL}/api/track/seller-click`, { method: 'POST', body: trackPayload, headers: { 'Content-Type': 'application/json' }, keepalive: true }).catch(() => { });
    }
    if (action.type === 'whatsapp') window.location.href = action.url;
    else window.open(action.url, '_blank', 'noopener,noreferrer');
    e.preventDefault();
  };

  const getSellerIcon = (type) => {
    if (type === 'whatsapp') return <MessageCircle className="w-4 h-4" />;
    if (type === 'instagram') return <ExternalLink className="w-4 h-4" />;
    return <Globe className="w-4 h-4" />;
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--bg-section)' }} data-testid="verify-page">
      <Header />

      <main className="flex-1 px-4 py-8 md:py-12">
        <div className="max-w-2xl mx-auto space-y-5" data-testid="verify-certificate">

          {/* ── 1. Verification Header (green premium card) ── */}
          <section
            className="rounded-xl p-6 md:p-8 text-center shadow-sm"
            style={{
              background: isPassed
                ? 'linear-gradient(135deg, var(--green-main) 0%, var(--green-light) 100%)'
                : '#7A1F1F',
            }}
            data-testid="verify-header"
          >
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/15 mb-3" aria-hidden="true">
              {isPassed ? <ShieldCheck className="w-6 h-6 text-white" /> : <ShieldX className="w-6 h-6 text-white" />}
            </div>
            <p className="text-[10px] font-black uppercase tracking-widest text-white/70 mb-2">Ürün Doğrulama Sayfası</p>
            <h1 className="text-lg md:text-2xl font-black text-white tracking-tight leading-tight mb-3" data-testid="verify-title">
              {isPassed
                ? 'Bu ürün TestettiK® bağımsız test süreciyle değerlendirilmiştir.'
                : 'Bu ürün TestettiK® test kriterlerini karşılayamamıştır.'}
            </h1>
            <div className="inline-flex items-center gap-2 text-[11px] font-medium text-white/85">
              <Hash className="w-3 h-3" />
              <span className="font-mono font-bold tracking-wider">{data.verify_slug || slug}</span>
              {data.created_at && (
                <>
                  <span className="text-white/40">·</span>
                  <span>Test Tarihi: {formatDateTR(data.created_at)}</span>
                </>
              )}
            </div>
          </section>

          {/* ── 2. Product Summary + Score ── */}
          <SectionCard icon={<Sparkles className="w-4 h-4" />} title={productName} subtitle={brandName || undefined} testid="verify-product-card">
            {data.image_url && (
              <div className="rounded-lg overflow-hidden mb-4 border" style={{ borderColor: 'var(--border-soft)', background: 'var(--bg-soft)' }}>
                <img src={data.image_url} alt={productName} className="w-full h-auto max-h-[320px] object-contain" />
              </div>
            )}

            <div className="flex items-center gap-4 flex-wrap mb-3" data-testid="verify-score-block">
              <div className="inline-flex items-baseline gap-1 px-5 py-3 rounded-lg" style={{ background: data.score_color || (isPassed ? '#15803d' : '#991b1b') }}>
                <span className="text-white text-3xl md:text-4xl font-black" data-testid="verify-score">{Number(data.score || 0).toFixed(1)}</span>
                <span className="text-white/70 text-sm"> / 10</span>
              </div>
              <div>
                <span className="inline-block px-3 py-1.5 text-white text-xs font-black rounded-md tracking-wide" style={{ background: isPassed ? '#15803d' : '#991b1b' }} data-testid="verify-badge">
                  {data.verdict_display || (isPassed ? 'TEST BAŞARILI' : 'BEKLENTİYİ KARŞILAMADI')}
                </span>
                <p className="text-xs mt-1.5 font-semibold" style={{ color: 'var(--text-secondary-strong)' }}>
                  {isPassed
                    ? 'TestettiK® değerlendirme sürecine göre başarılı bulunmuştur.'
                    : 'Bu ürün belirlenen test kriterlerini karşılamamıştır.'}
                </p>
              </div>
            </div>

            {/* Secondary seller CTA — small, not dominant */}
            {hasSellerAction && (
              <div className="mt-4 pt-4 border-t flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: 'var(--border-soft)' }}>
                <p className="text-xs font-medium flex-1 min-w-[140px]" style={{ color: 'var(--text-secondary)' }}>
                  Satıcı ile iletişime geçmek ister misiniz?
                </p>
                <a
                  href={sellerAction.url}
                  onClick={(e) => handleSellerClick(e, sellerAction)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold rounded-md border transition hover:bg-neutral-50"
                  style={{ borderColor: 'var(--border-soft)', color: 'var(--text-primary)' }}
                  data-testid="seller-action-button"
                >
                  {getSellerIcon(sellerAction.type)}
                  {sellerAction.type === 'instagram' ? 'Satıcı Profilini Gör' : sellerAction.label}
                </a>
              </div>
            )}
          </SectionCard>

          {/* ── 3. Video ── */}
          {canRenderPublicVideo(data.video) ? (
            <SectionCard icon={<FileText className="w-4 h-4" />} title="TestettiK Video Kanıtı" subtitle="Ürünün TestettiK bağımsız test süreci kapsamında hazırlanmıştır." testid="verify-video-section">
              <TestettiKVideoPlayer video={data.video} title={`${productName} — TestettiK Video Kanıtı`} />
            </SectionCard>
          ) : data.video_url ? (
            <SectionCard icon={<FileText className="w-4 h-4" />} title="Test Videosu" testid="verify-video-section">
              <VideoPlayer url={data.video_url} title={`${productName} test videosu`} />
            </SectionCard>
          ) : (
            <SectionCard icon={<FileText className="w-4 h-4" />} title="Test Videosu" testid="verify-video-empty">
              <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Bu ürün için test videosu henüz eklenmemiştir.</p>
            </SectionCard>
          )}

          {/* ── 4. Why this score ── */}
          <SectionCard icon={<CheckCircle2 className="w-4 h-4" />} title="Neden bu puanı aldı?" testid="verify-why-section">
            {(data.short_description || data.description) ? (
              <div className="text-[15px] leading-relaxed font-medium space-y-2" style={{ color: 'var(--text-secondary-strong)' }}>
                {data.short_description && (
                  <p className="font-bold" style={{ color: 'var(--text-primary)' }}>{data.short_description}</p>
                )}
                {data.description && data.description !== data.short_description && (
                  <p style={{ whiteSpace: 'pre-line' }}>{data.description}</p>
                )}
              </div>
            ) : (
              <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                Bu ürünün detaylı puan açıklaması yakında bu sayfada gösterilecektir.
              </p>
            )}
          </SectionCard>

          {/* ── 5. Test Procedure ── */}
          {data.test_procedure ? (
            <SectionCard icon={<ClipboardCheck className="w-4 h-4" />} title="Uygulanan Test Prosedürü" subtitle={data.test_procedure.category} testid="verify-procedure-section">
              <p className="text-base md:text-lg font-black mb-2" style={{ color: 'var(--green-main)' }}>{data.test_procedure.title}</p>
              <p className="text-sm leading-relaxed font-medium mb-4" style={{ color: 'var(--text-secondary-strong)' }}>{data.test_procedure.description}</p>
              {data.test_procedure.criteria_list && data.test_procedure.criteria_list.length > 0 && (
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="verify-procedure-criteria">
                  {data.test_procedure.criteria_list.map((c, i) => (
                    <li key={i} className="flex items-start gap-2 border rounded-lg p-2.5" style={{ borderColor: 'var(--border-soft)', background: 'var(--bg-section)' }}>
                      <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--green-main)' }} />
                      <span className="text-sm font-medium" style={{ color: 'var(--text-secondary-strong)' }}>{c}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex items-center gap-3 pt-3 mt-4 border-t text-xs flex-wrap" style={{ borderColor: 'var(--border-soft)', color: 'var(--text-muted)' }}>
                <span>Versiyon: <span className="font-bold" style={{ color: 'var(--text-secondary-strong)' }}>v{data.test_procedure.version}</span></span>
                {data.test_procedure.last_updated_at && (
                  <span>Son güncelleme: <span className="font-bold" style={{ color: 'var(--text-secondary-strong)' }}>{formatDateTR(data.test_procedure.last_updated_at)}</span></span>
                )}
              </div>
            </SectionCard>
          ) : (
            <SectionCard icon={<ClipboardCheck className="w-4 h-4" />} title="Uygulanan Test Prosedürü" subtitle={data.category || undefined} testid="verify-procedure-empty">
              <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                Bu kategori için test prosedürü yakında yayınlanacaktır.
              </p>
            </SectionCard>
          )}

          {/* ── 6. Güvenİzi ── */}
          <SectionCard icon={<History className="w-4 h-4" />} title="Güvenİzi" subtitle="Bu ürünün puan geçmişi ve değerlendirme değişiklikleri" testid="verify-guveniz-section">
            {data.score_history && data.score_history.length > 0 ? (
              data.score_history.length === 1 ? (
                <div className="border-l-2 pl-4 py-1" style={{ borderColor: 'var(--green-main)' }} data-testid="verify-guveniz-single">
                  <p className="text-xs font-bold uppercase mb-1" style={{ color: 'var(--text-muted)' }}>{formatDateTR(data.score_history[0].created_at)}</p>
                  <p className="text-sm font-bold mb-1.5" style={{ color: 'var(--green-main)' }}>{data.score_history[0].source}</p>
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-secondary-strong)' }}>İlk değerlendirme puanı oluşturuldu.</p>
                </div>
              ) : (
                <ol className="relative border-l-2 pl-4 space-y-4" style={{ borderColor: 'var(--border-soft)' }} data-testid="verify-guveniz-timeline">
                  {data.score_history.map((h, idx) => (
                    <li key={idx} className="relative">
                      <span className={`absolute -left-[21px] w-2.5 h-2.5 rounded-full`} style={{
                        background: idx === 0 ? 'var(--green-main)' : '#D1D5DB',
                      }} />
                      <p className="text-[11px] font-bold uppercase mb-0.5" style={{ color: 'var(--text-muted)' }}>{formatDateTR(h.created_at)}</p>
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        {h.old_score != null && <span className="text-xs font-mono line-through" style={{ color: 'var(--text-muted)' }}>{Number(h.old_score).toFixed(1)}</span>}
                        <span className="text-sm font-mono font-black" style={{ color: 'var(--text-primary)' }}>{Number(h.new_score).toFixed(1)} <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>/ 10</span></span>
                      </div>
                      <p className="text-xs font-bold" style={{ color: 'var(--green-main)' }}>{h.source}</p>
                      <p className="text-xs font-medium" style={{ color: 'var(--text-secondary-strong)' }}>{h.change_reason}</p>
                    </li>
                  ))}
                </ol>
              )
            ) : (
              <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                Bu ürün için ilk doğrulama kaydı gösterilmektedir.
              </p>
            )}
          </SectionCard>

          {/* ── 7. Verification Metadata ── */}
          <section className="border rounded-xl p-5 md:p-6 bg-white shadow-sm" style={{ borderColor: 'var(--border-soft)' }} data-testid="verify-metadata">
            <p className="text-[10px] font-black uppercase tracking-widest mb-3" style={{ color: 'var(--text-muted)' }}>Sertifika Bilgileri</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3 text-xs">
              <div>
                <p className="font-black uppercase mb-0.5" style={{ color: 'var(--text-muted)' }}>Doğrulama Kodu</p>
                <p className="font-mono font-bold" style={{ color: 'var(--text-primary)' }}>{data.verify_slug || slug}</p>
              </div>
              {data.category && (
                <div>
                  <p className="font-black uppercase mb-0.5" style={{ color: 'var(--text-muted)' }}>Kategori</p>
                  <p className="font-semibold" style={{ color: 'var(--text-secondary-strong)' }}>{data.category}</p>
                </div>
              )}
              {data.created_at && (
                <div>
                  <p className="font-black uppercase mb-0.5" style={{ color: 'var(--text-muted)' }}>Test Tarihi</p>
                  <p className="font-semibold flex items-center gap-1" style={{ color: 'var(--text-secondary-strong)' }}>
                    <Clock className="w-3 h-3" /> {formatDateTR(data.created_at)}
                  </p>
                </div>
              )}
              {data.test_procedure?.version && (
                <div>
                  <p className="font-black uppercase mb-0.5" style={{ color: 'var(--text-muted)' }}>Prosedür Versiyonu</p>
                  <p className="font-semibold" style={{ color: 'var(--text-secondary-strong)' }}>v{data.test_procedure.version}</p>
                </div>
              )}
              {data.badge_code && (
                <div>
                  <p className="font-black uppercase mb-0.5" style={{ color: 'var(--text-muted)' }}>Rozet Kodu</p>
                  <p className="font-mono font-bold" style={{ color: 'var(--text-primary)' }}>{data.badge_code}</p>
                </div>
              )}
              <div>
                <p className="font-black uppercase mb-0.5" style={{ color: 'var(--text-muted)' }}>Durum</p>
                <p className="font-bold" style={{ color: isPassed ? 'var(--green-main)' : '#991b1b' }}>{isPassed ? 'Geçerli' : 'Başarısız'}</p>
              </div>
            </div>
          </section>

          {/* ── 8. Footer Trust Notice (darker, readable) ── */}
          <div className="text-center pt-4" data-testid="verify-footer">
            <p className="text-sm font-bold leading-relaxed mb-2" style={{ color: 'var(--text-primary)' }} data-testid="verify-footer-primary">
              Bu değerlendirme TestettiK® bağımsız test süreci ile oluşturulmuştur.
            </p>
            <p className="text-xs font-medium leading-relaxed" style={{ color: 'var(--text-secondary-strong)' }} data-testid="verify-footer-note">
              TestettiK®, ürünleri bağımsız olarak test eder. Markalarla ticari ortaklık kurulmaz.
            </p>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default VerifyPage;
